import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { DashboardTemplate } from '@parashift/shared/models';

/** What `DashboardAdminComponent` hands in via `initialState.model` when opening the modal. */
export interface TemplateEditorInput {
  editingTemplate: DashboardTemplate | null; // null = adding a new template
  nextPosition: number; // default `position` for a new template
}

/** The collected form values, handed back to `DashboardAdminComponent` to persist. */
export interface TemplateEditorResult {
  id: string | null; // null = new
  key: string;
  name: string;
  visibility: string; // 'public' | 'tenant' | 'sub_tenants'
  enabled: boolean;
  position: number;
}

@Component({
  selector: 'template-editor-modal',
  templateUrl: './template-editor-modal.component.html',
  styleUrls: ['./template-editor-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule]
})
export class TemplateEditorModalComponent implements OnInit {
  bsModalRef = inject(BsModalRef);

  // Holds TemplateEditorInput on open, then TemplateEditorResult once save() overwrites it.
  model: unknown = null;
  /** Read by `ModalService` on close — true only when Save was clicked. */
  state = false;

  private editingTemplate: DashboardTemplate | null = null;

  readonly isEdit = computed(() => !!this.editingTemplate);

  readonly key = signal('');
  readonly name = signal('');
  readonly visibility = signal('public');
  readonly position = signal(0);
  readonly enabled = signal(true);

  ngOnInit(): void {
    const input = this.model as TemplateEditorInput | null;
    this.editingTemplate = input?.editingTemplate ?? null;

    const template = this.editingTemplate;
    if (!template) {
      this.position.set(input?.nextPosition ?? 0);
      return;
    }
    this.key.set(template.key);
    this.name.set(template.name);
    this.visibility.set(template.visibility);
    this.position.set(template.position ?? 0);
    this.enabled.set(template.enabled);
  }

  save(): void {
    this.model = {
      id: this.editingTemplate?.id ?? null,
      key: this.key(),
      name: this.name(),
      visibility: this.visibility(),
      enabled: this.enabled(),
      position: Number(this.position()) || 0
    };
    this.state = true;
    this.bsModalRef.hide();
  }

  close(): void {
    this.bsModalRef.hide();
  }
}
